import { useState, useEffect } from "react";
import { MessageCircle, X, Mail } from "lucide-react";
import { EnquiryDialog } from "@/components/EnquiryDialog";

/**
 * Floating chat-style entry to message Clean Kin.
 *
 * Tapping the bubble opens a friendly welcome card; tapping "Email us"
 * opens the unified enquiry form which captures the message to MongoDB
 * and forwards it to info@cleankinservices.co.uk.
 *
 * The email address itself is never displayed (per brand requirement).
 */
export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);

  // Auto-pop the teaser bubble 6 seconds after first paint, then hide after 6s.
  useEffect(() => {
    const showTimer = setTimeout(() => setShowTeaser(true), 6000);
    const hideTimer = setTimeout(() => setShowTeaser(false), 18000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Teaser callout (auto-pop) */}
      {showTeaser && !open && (
        <div
          className="bg-white shadow-xl rounded-2xl rounded-br-sm p-4 max-w-xs border border-[#E2E8E0] animate-in slide-in-from-bottom-2"
          data-testid="chat-teaser"
        >
          <button
            type="button"
            onClick={() => setShowTeaser(false)}
            aria-label="Dismiss"
            className="absolute -top-2 -right-2 bg-white rounded-full w-6 h-6 flex items-center justify-center shadow border border-[#E2E8E0] text-[#5C6B57] hover:text-[#1A2E12]"
            style={{ position: "absolute" }}
          >
            <X className="w-3 h-3" />
          </button>
          <p className="text-sm text-[#1A2E12] font-medium pr-4">
            Hey there and welcome to Clean Kin! Do you have a question?
          </p>
        </div>
      )}

      {/* Expanded card */}
      {open && (
        <div
          className="bg-white shadow-2xl rounded-2xl border border-[#E2E8E0] overflow-hidden w-[300px]"
          data-testid="chat-widget-card"
        >
          <div className="bg-[#2D5016] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#7CB342] flex items-center justify-center font-bold text-sm">
                CK
              </div>
              <div>
                <p className="font-semibold text-sm leading-tight">Clean Kin</p>
                <p className="text-xs text-white/70 leading-tight">
                  Typically replies within 1 hour
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="text-white/80 hover:text-white"
              data-testid="chat-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-3 bg-[#F9FAF9]">
            <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm border border-[#E2E8E0]">
              <p className="text-sm text-[#1A2E12]">
                Hey there and welcome to Clean Kin! Do you have a question?
              </p>
            </div>
            <p className="text-xs text-[#5C6B57] text-center pt-1">
              We&apos;ll get back to you within 1 business day.
            </p>
          </div>

          <EnquiryDialog source="chat-widget" subject="Question for Clean Kin">
            <button
              type="button"
              className="block w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white text-center py-3 font-semibold transition-colors"
              data-testid="chat-email-cta"
              aria-label="Email Clean Kin"
            >
              <span className="inline-flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email us your question
              </span>
            </button>
          </EnquiryDialog>
        </div>
      )}

      {/* Floating button */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setShowTeaser(false);
        }}
        aria-label="Open chat with Clean Kin"
        data-testid="chat-bubble-button"
        className="w-14 h-14 rounded-full bg-[#7CB342] hover:bg-[#689F38] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </div>
  );
};

export default ChatWidget;
